import { useTimer } from "../context/TimerContext";
import { createPortal } from "react-dom";
import { 
  X, 
  Pause, 
  Play, 
  Plus, 
  Minus,
  Minimize2,
  Maximize2,
  Timer,
  ChevronRight
} from "lucide-react";
import { cn } from "../lib/utils";

const GlobalRestTimer = () => {
  const {
    isActive,
    timeLeft,
    totalTime,
    exerciseName,
    nextExerciseName,
    isMinimized,
    pauseTimer,
    resumeTimer,
    stopTimer,
    adjustTime,
    minimizeTimer,
    maximizeTimer,
  } = useTimer();

  // Don't render if timer is not running and time is 0
  if (timeLeft === 0 && !isActive) {
    return null;
  }

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = totalTime > 0 ? ((totalTime - timeLeft) / totalTime) * 100 : 0;

  // Minimized floating timer
  if (isMinimized) {
    const timerContent = (
      <button
        onClick={maximizeTimer}
        className="fixed bottom-24 right-4 z-[90] bg-[#D4AF37] text-[#0B0B0B] rounded-full w-16 h-16 flex flex-col items-center justify-center shadow-lg shadow-[#D4AF37]/30 animate-pulse-slow"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}
        data-testid="minimized-timer"
      >
        <Timer className="w-5 h-5" />
        <span className="text-xs font-bold">
          {minutes}:{seconds.toString().padStart(2, "0")}
        </span>
      </button>
    );
    
    return createPortal(timerContent, document.body);
  }

  // Full screen timer
  const timerContent = (
    <div 
      className="fixed inset-0 z-[95] flex items-center justify-center bg-[#0B0B0B]"
      style={{ 
        paddingTop: 'env(safe-area-inset-top)', 
        paddingBottom: 'env(safe-area-inset-bottom)' 
      }}
      data-testid="rest-timer-fullscreen"
    >
      {/* Close / Minimize buttons */}
      <div 
        className="absolute top-4 right-4 flex items-center gap-2"
        style={{ marginTop: 'env(safe-area-inset-top)' }}
      >
        <button
          onClick={minimizeTimer}
          className="p-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          data-testid="minimize-timer-btn"
        >
          <Minimize2 className="w-5 h-5 text-[#888]" />
        </button>
        <button
          onClick={stopTimer}
          className="p-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          data-testid="close-timer-btn"
        >
          <X className="w-5 h-5 text-[#888]" />
        </button>
      </div>

      <div className="w-full max-w-sm mx-4 text-center">
        {/* Exercise info */}
        {exerciseName && (
          <div className="mb-6">
            <p className="text-[#888] text-sm uppercase tracking-wider mb-1">Descanso después de</p>
            <p className="text-white text-lg font-heading font-bold">{exerciseName}</p>
          </div>
        )}

        {/* Circular progress */}
        <div className="relative w-64 h-64 mx-auto mb-8">
          {/* Background circle */}
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="128"
              cy="128"
              r="120"
              stroke="#1A1A1A"
              strokeWidth="8"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="128"
              cy="128"
              r="120"
              stroke={timeLeft <= 3 ? "#EF4444" : "#D4AF37"}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 120}
              strokeDashoffset={2 * Math.PI * 120 * (1 - progress / 100)}
              className="transition-all duration-1000 ease-linear"
            />
          </svg>
          
          {/* Time display */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={cn(
              "text-7xl font-heading font-black tabular-nums transition-colors",
              timeLeft <= 3 ? "text-red-500 animate-pulse" : "text-white"
            )}>
              {minutes}:{seconds.toString().padStart(2, "0")}
            </span>
            <span className="text-[#666] text-sm mt-2">
              {isActive ? "Descansando..." : "Pausado"}
            </span>
          </div>
        </div>

        {/* Time adjustment */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <button
            onClick={() => adjustTime(-15)}
            className="w-14 h-14 rounded-2xl bg-[#1A1A1A] hover:bg-[#2A2A2A] flex items-center justify-center transition-colors"
            data-testid="timer-minus-15"
          >
            <Minus className="w-5 h-5 text-[#888]" />
            <span className="text-[#888] text-xs ml-0.5">15</span>
          </button>
          
          <button
            onClick={isActive ? pauseTimer : resumeTimer}
            className={cn(
              "w-20 h-20 rounded-full flex items-center justify-center transition-all",
              isActive 
                ? "bg-[#D4AF37] hover:bg-[#E5C54D]" 
                : "bg-[#22C55E] hover:bg-[#16A34A]"
            )}
            data-testid="timer-play-pause"
          >
            {isActive ? (
              <Pause className="w-8 h-8 text-[#0B0B0B]" />
            ) : (
              <Play className="w-8 h-8 text-white ml-1" />
            )}
          </button>
          
          <button
            onClick={() => adjustTime(15)}
            className="w-14 h-14 rounded-2xl bg-[#1A1A1A] hover:bg-[#2A2A2A] flex items-center justify-center transition-colors"
            data-testid="timer-plus-15"
          >
            <Plus className="w-5 h-5 text-[#888]" />
            <span className="text-[#888] text-xs ml-0.5">15</span>
          </button>
        </div>

        {/* Next exercise hint */}
        {nextExerciseName && (
          <div className="bg-[#1A1A1A] rounded-2xl p-4 flex items-center justify-between">
            <div className="text-left">
              <p className="text-[#666] text-xs uppercase tracking-wider">Siguiente</p>
              <p className="text-white font-semibold">{nextExerciseName}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
          </div>
        )}

        {/* Skip button */}
        <button
          onClick={stopTimer}
          className="mt-6 text-[#666] hover:text-white text-sm transition-colors"
        >
          Saltar descanso
        </button>
      </div>
    </div>
  );

  return createPortal(timerContent, document.body);
};

export default GlobalRestTimer;
